import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {BranchListResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteResponse,
  GetWarehousesResponse,
  usePatchReceiveNoteCompletedMutation,
  usePatchReceiveNoteManualMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromBranches,
  getOptionsFromWarehouses,
  handleApiError,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {ApiError, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  TransferReceiveNoteForm,
  TransferReceiveNoteFormSchema,
} from '../types/TransferReceiveNoteForm';

export interface BranchBasedInformationFormProps extends RequiredTestIdProps {
  isCompleted: boolean;
  receiveNote: GetReceiveNoteResponse | Nullish;
  branchList: BranchListResponseBody | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes?: PaymentTypeResponseBody[] | Nullish;
  onClose: () => void;
}

export function BranchBasedInformationForm(props: BranchBasedInformationFormProps) {
  const [patchReceiveNote] = usePatchReceiveNoteManualMutation();
  const [patchReceiveNoteCompleted] = usePatchReceiveNoteCompletedMutation();

  const isOpeningBalance = props.receiveNote?.type === 'OPENING_BALANCE';

  const defaultValues: Partial<TransferReceiveNoteForm> = {
    sourceBranchId: props.receiveNote?.transferSourceBranchId,
    warehouseId: props.receiveNote?.warehouseId,
    authorizationProfileId: props.receiveNote?.authorizationProfileId,
    receiveNoteNumber: props.receiveNote?.receiveNoteNumber,
    invoiceIssueDate: parseDate(props.receiveNote?.invoiceIssueDate),
    deliveryNoteNumber: props.receiveNote?.deliveryNote?.deliveryNoteNumber,
    deliveryNoteIssuedAt: parseDate(props.receiveNote?.deliveryNote?.deliveryNoteIssuedAt),
    paymentType: props.receiveNote?.paymentType,
    note: props.receiveNote?.note,
  };

  const handleSubmit: FormSubmitHandler<TransferReceiveNoteForm> = async (
    formValues,
    setErrors,
    reset,
    blockerContext
  ) => {
    if (isNil(props.receiveNote)) {
      return;
    }

    if (props.isCompleted) {
      return await patchReceiveNoteCompleted({
        receiveNoteId: props.receiveNote.receiveNoteId,
        body: {
          deliveryNoteNumber: formValues.deliveryNoteNumber,
          invoiceIssueDate: formValues.invoiceIssueDate?.toISOString(),
          issuedAt: formValues.deliveryNoteIssuedAt?.toISOString(),
          note: formValues.note,
        },
      })
        .unwrap()
        .then(props.onClose)
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchReceiveNote({
      receiveNoteId: props.receiveNote.receiveNoteId,
      body: {
        transferSourceBranchId: formValues.sourceBranchId,
        warehouseId: formValues.warehouseId,
        authorizationProfileId: formValues.authorizationProfileId,
        receiveNoteNumber: formValues.receiveNoteNumber,
        invoiceIssueDate: formValues.invoiceIssueDate?.toISOString(),
        deliveryNoteNumber: formValues.deliveryNoteNumber,
        issuedAt: formValues.deliveryNoteIssuedAt?.toISOString(),
        paymentType: formValues.paymentType,
        note: formValues.note,
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<TransferReceiveNoteForm>
      experimentalZodSchema={TransferReceiveNoteFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      shouldWatchForUnsavedChanges
    >
      {(control) => (
        <>
          <VStack spacing={4}>
            <Grid columns={4}>
              <FormField
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="sourceBranchId"
                label={i18n.t('entity.warehouse.labels.sourceBranch')}
                options={getOptionsFromBranches(props.branchList)}
                data-testid={suffixTestId('inputs.sourceBranchId', props)}
              />
              <FormField
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="warehouseId"
                label={i18n.t('entity.warehouse.labels.warehouse')}
                options={getOptionsFromWarehouses(props.warehouses)}
                data-testid={suffixTestId('inputs.warehouseId', props)}
              />
              <FormField
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="authorizationProfileId"
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(props.authorizationProfiles)}
                data-testid={suffixTestId('inputs.authorizationProfileId', props)}
              />
              <FormField
                isDisabled
                control={control}
                type="text"
                name="receiveNoteNumber"
                label={i18n.t('entity.warehouse.labels.number')}
                data-testid={suffixTestId('inputs.receiveNoteNumber', props)}
              />
            </Grid>
            <Grid columns={4}>
              <FormField
                isDisabled={!isOpeningBalance}
                control={control}
                type="date"
                name="invoiceIssueDate"
                label={i18n.t('entity.warehouse.labels.date')}
                data-testid={suffixTestId('inputs.date', props)}
              />
              <FormField
                isDisabled={!isOpeningBalance}
                control={control}
                type="text"
                name="deliveryNoteNumber"
                label={
                  isOpeningBalance
                    ? i18n.t('entity.warehouse.labels.openingBalanceNo')
                    : i18n.t('entity.warehouse.labels.transferOrderNo')
                }
                data-testid={suffixTestId('inputs.transferOrderNumber', props)}
              />
              <FormField
                isDisabled
                control={control}
                type="date"
                name="deliveryNoteIssuedAt"
                label={
                  isOpeningBalance
                    ? i18n.t('entity.warehouse.labels.openingBalanceDate')
                    : i18n.t('entity.warehouse.labels.transferOrderDate')
                }
                data-testid={suffixTestId('inputs.transferOrderDate', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled
                type="choice"
                name="paymentType"
                label={i18n.t('entity.warehouse.labels.paymentType')}
                options={getOptionsFromPaymentTypes(props.paymentTypes)}
                control={control}
                data-testid={suffixTestId('inputs.paymentType', props)}
              />
            </Grid>
            <Grid columns={4}>
              <GridItem span={4}>
                <FormField
                  control={control}
                  type="textarea"
                  name="note"
                  label={i18n.t('entity.warehouse.labels.note')}
                  data-testid={suffixTestId('inputs.note', props)}
                />
              </GridItem>
            </Grid>
          </VStack>

          <Separator />

          <HStack justify="flex-end">
            <ButtonGroup>
              <Show when={isNotNil(props.receiveNote)}>
                <FormButton
                  control={control}
                  type="reset"
                  variant="secondary"
                  title={i18n.t('general.labels.discard')}
                  onClick={props.onClose}
                  data-testid={suffixTestId('actions.discard', props)}
                />
              </Show>
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('general.labels.saveChanges')}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </HStack>
        </>
      )}
    </Form>
  );
}
